const getListConfig = () => {
  const container = document.getElementById('paginated-list');
  if (!container) {
    return null;
  }

  const subcategoryId = container.dataset.subcategoryId;
  const recipeIds = (container.dataset.recipeIds || '').split(',').filter(Boolean);
  const paginationLimit = parseInt(container.dataset.paginationLimit, 10) || 12;

  if (!subcategoryId && 0 === recipeIds.length) {
    return null;
  }

  const locale = container.dataset.locale || '';

  return ({
    container,
    subcategoryId,
    recipeIds,
    paginationLimit,
    translationLocale: locale !== 'nl' ? locale : null
  });
};

const fetchListPage = (config, cursor) => {
  if (0 < config.recipeIds.length) {
    return window.RecipeAPI.fetchRecipePageByIds(
      config.recipeIds,
      config.paginationLimit,
      cursor,
      config.translationLocale
    );
  }

  return window.RecipeAPI.fetchRecipePage(
    config.subcategoryId,
    config.paginationLimit,
    cursor,
    config.translationLocale
  );
};

const getCardTemplate = () => {
  return document.getElementById('recipe-card-template');
};

const sanitizeCssUrl = (url) => {
  try {
    const parsed = new URL(url);
    if ('https:' !== parsed.protocol && 'http:' !== parsed.protocol) {
      return '';
    }
    return parsed.href;
  } catch {
    return '';
  }
};

const preloadCardImage = (imgBg, imageUrl, altText) => {
  if (!imageUrl) {
    return;
  }

  const safeUrl = sanitizeCssUrl(imageUrl);
  if (!safeUrl) {
    return;
  }

  const img = imgBg.querySelector('[data-role="img"]');
  if (!img) {
    return;
  }

  imgBg.classList.add('recipe-list-card-img-bg--preloading');
  img.alt = altText || '';

  const handleImageReady = () => {
    imgBg.classList.add('recipe-list-card-img-bg--loaded');
  };
  img.addEventListener('load', handleImageReady);
  img.addEventListener('error', handleImageReady);
  img.src = safeUrl;
};

const populateCard = (card, recipe, config) => {
  const imageUrl = recipe.image || config.placeholderImage;
  const pageHandle = recipe.page?.handle || '';
  const recipeUrl = pageHandle ? `/pages/${pageHandle}` : '#';

  const imgBg = card.querySelector('[data-role="image"]');
  if (imgBg) {
    preloadCardImage(imgBg, imageUrl, recipe.title);
  }

  card.setAttribute('cad-object-id', recipe.id);

  const links = card.querySelectorAll('[data-role="link"]');
  links.forEach((link) => {
    link.setAttribute('href', recipeUrl);
  });

  const titleEl = card.querySelector('[data-role="title"]');
  if (titleEl) {
    titleEl.textContent = recipe.title || '';
  }

  const cookTimeEl = card.querySelector('[data-role="cooktime"]');
  if (cookTimeEl) {
    if (!recipe.cookTime) {
      cookTimeEl.classList.add('hidden');
    }
    const cookTimeValue = cookTimeEl.querySelector('[data-role="cooktime-value"]');
    if (cookTimeValue) {
      cookTimeValue.textContent = recipe.cookTime || '';
    }
  }

  const difficultyEl = card.querySelector('[data-role="difficulty"]');
  if (difficultyEl) {
    if (!recipe.difficulty) {
      difficultyEl.classList.add('hidden');
    }
    populateDifficultyBars(difficultyEl, recipe.difficulty, config.easyLabel, config.mediumLabel);
    const difficultyValue = difficultyEl.querySelector('[data-role="difficulty-value"]');
    if (difficultyValue) {
      difficultyValue.textContent = recipe.difficulty || '';
    }
  }
};

const populateDifficultyBars = (difficultyEl, difficulty, easyLabel, mediumLabel) => {
  if (!difficulty) {
    return;
  }

  const isEasy = difficulty.toLowerCase().includes(easyLabel.toLowerCase());
  const isMedium = difficulty.toLowerCase().includes(mediumLabel.toLowerCase());

  const barMedium = difficultyEl.querySelector('[data-role="bar-medium"]');
  const barHard = difficultyEl.querySelector('[data-role="bar-hard"]');

  if (barMedium) {
    barMedium.setAttribute('fill', isEasy ? '#aaa' : '#333');
  }
  if (barHard) {
    barHard.setAttribute('fill', (isEasy || isMedium) ? '#aaa' : '#333');
  }
};

const renderRecipeCards = (container, template, recipes, cardConfig) => {
  const fragment = document.createDocumentFragment();

  recipes.forEach((recipe) => {
    const clone = template.content.cloneNode(true);
    const card = clone.querySelector('.recipe-list-card');
    if (!card) {
      return;
    }

    populateCard(card, recipe, cardConfig);
    fragment.appendChild(clone);
  });

  container.appendChild(fragment);
};

const showLoadingSkeletons = (container, template, count) => {
  for (let i = 0; i < count; i++) {
    const clone = template.content.cloneNode(true);
    const card = clone.querySelector('.recipe-list-card');
    if (card) {
      card.classList.add('loading');
      card.classList.remove('CAD-wishlist-button');
    }
    container.appendChild(clone);
  }
};

const clearContainer = (container) => {
  container.replaceChildren();
};

const notifySectionRendered = (container) => {
  document.dispatchEvent(
    new CustomEvent('shopify:section:load', {
      detail: { sectionId: container.closest('[data-section-id]')?.dataset.sectionId }
    })
  );
};

const initRecipeList = async () => {
  const config = getListConfig();
  if (!config) {
    return;
  }

  const template = getCardTemplate();
  if (!template) {
    return;
  }

  const pagination = window.RecipePagination;

  const cardConfig = {
    placeholderImage: config.container.dataset.placeholderImage || '',
    easyLabel: config.container.dataset.difficultyEasy || '',
    mediumLabel: config.container.dataset.difficultyMedium || ''
  };

  const paginationState = pagination.createPaginationState();

  const loadPage = async (pageNumber, shouldScroll = false) => {
    const cursor = pagination.getCursorForPage(paginationState, pageNumber);
    if (cursor === undefined) {
      return;
    }

    clearContainer(config.container);
    showLoadingSkeletons(config.container, template, config.paginationLimit);

    try {
      const pageData = await fetchListPage(config, cursor);

      clearContainer(config.container);
      renderRecipeCards(config.container, template, pageData.items, cardConfig);
      notifySectionRendered(config.container);

      pagination.updatePaginationState(paginationState, pageNumber, pageData.hasNextPage, pageData.endCursor);
      paginationState.currentPage = pageNumber;

      pagination.updatePaginationButtons(
        1 < paginationState.currentPage,
        pageData.hasNextPage,
        () => loadPage(paginationState.currentPage - 1, true),
        () => loadPage(paginationState.currentPage + 1, true)
      );

      pagination.renderPaginationNumbers(paginationState, (page) => loadPage(page, true));
      pagination.updatePaginationVisibility(paginationState.currentPage, paginationState.highestKnownPage, paginationState.hasMorePages);
      pagination.updateUrlPage(pageNumber);

      if (shouldScroll) {
        config.container.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      clearContainer(config.container);
      config.container.textContent = config.container.dataset.errorMessage || 'Something went wrong loading recipes.';
    }
  };

  const loadDeepLinkedPage = async (targetPage) => {
    clearContainer(config.container);
    showLoadingSkeletons(config.container, template, config.paginationLimit);

    const pageSequence = Array.from({ length: targetPage }, (_, i) => i + 1);

    for (const page of pageSequence) {
      const cursor = pagination.getCursorForPage(paginationState, page);
      if (cursor === undefined) {
        console.warn(`Deep-link: cursor unavailable for page ${page}, falling back to page 1`);
        break;
      }

      const pageData = await fetchListPage(config, cursor);

      pagination.updatePaginationState(paginationState, page, pageData.hasNextPage, pageData.endCursor);
      paginationState.currentPage = page;

      if (page < targetPage && pageData.hasNextPage) {
        continue;
      }

      clearContainer(config.container);
      renderRecipeCards(config.container, template, pageData.items, cardConfig);
      notifySectionRendered(config.container);

      pagination.updatePaginationButtons(
        1 < paginationState.currentPage,
        pageData.hasNextPage,
        () => loadPage(paginationState.currentPage - 1, true),
        () => loadPage(paginationState.currentPage + 1, true)
      );

      pagination.renderPaginationNumbers(paginationState, (p) => loadPage(p, true));
      pagination.updatePaginationVisibility(paginationState.currentPage, paginationState.highestKnownPage, paginationState.hasMorePages);
      pagination.updateUrlPage(page);
      return;
    }

    await loadPage(1, false);
  };

  const targetPage = pagination.getPageFromUrl();

  if (1 >= targetPage) {
    await loadPage(1, false);
    return;
  }

  try {
    await loadDeepLinkedPage(targetPage);
  } catch (error) {
    clearContainer(config.container);
    config.container.textContent = config.container.dataset.errorMessage || 'Something went wrong loading recipes.';
  }
};

document.addEventListener('DOMContentLoaded', initRecipeList);
